import { Cartao } from '../../../components/ListaDeDados'
import { cn } from '../../../lib/cn'
import { TERMOS, TEXTOS } from '../../../lib/glossario'
import type { RedutorNaTela } from '../../../types/chamado'

/**
 * O desconto do mês: a soma dos indicadores, o teto do contrato e o que sobra na nota.
 *
 * **A soma bruta aparece ao lado do valor aplicado, sempre.** É ela que explica por que o teto foi
 * acionado: em maio/2026 a soma foi 17% e o documento assinado registrou glosa de 10% sobre
 * R$ 182.870,37 — sem a soma à vista, o 10% parece um valor apurado quando é um limite, e a
 * diferença muda a conversa com a contratada.
 *
 * Os indicadores não apurados são listados por nome. Eles ficaram de fora da soma, e ficar de fora
 * não é o mesmo que somar zero: enquanto houver indicador pendente, este número não é o desconto do
 * mês — é o desconto do que deu para medir.
 *
 * @param props o redutor apurado
 * @returns o cartão do desconto
 */
export function RedutorDaFatura({ redutor }: { redutor: RedutorNaTela }) {
  return (
    <Cartao titulo={TERMOS.redutorDaFatura.rotulo} descricao={TERMOS.redutorDaFatura.ajuda}>
      <div className="space-y-4">
        <dl className="grid gap-3 sm:grid-cols-3">
          <Valor rotulo="Soma dos indicadores" valor={`${redutor.somaDosRedutores}%`} />
          <Valor rotulo={`Limite do contrato`} valor={`${redutor.tetoPercentual}%`} />
          <Valor
            rotulo="Desconto apurado"
            valor={`${redutor.redutorApurado}%`}
            destacar={redutor.redutorApurado > 0}
          />
        </dl>

        {redutor.tetoAlcancado && (
          <p className="max-w-prose rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {TEXTOS.fiscalizacao.tetoAlcancado}
          </p>
        )}

        {!redutor.definitivo && (
          <div className="max-w-prose rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-800">
            <p>
              <strong className="font-semibold">Este ainda não é o desconto do mês.</strong> Ficaram de fora da
              soma:{' '}
              <span className="font-medium">{redutor.naoApurados.join(', ')}</span>. Indicador que não foi apurado
              não vale zero — vale nada medido, e pode aumentar o desconto quando for apurado.
            </p>
          </div>
        )}

        {/* Sem dispensa o selo é mudo e o botão só aparece para fiscal em mês aberto: a frase diz o
            que vale, e aponta para onde a dispensa se registra — que existe desde a V23. */}
        {!redutor.sancoesDispensadas && (
          <p className="max-w-prose text-xs text-slate-600">{TEXTOS.fiscalizacao.semDispensa}</p>
        )}

        {redutor.sancoesDispensadas && (
          <p className="max-w-prose rounded-lg border border-slate-300 bg-slate-100 px-3 py-2 text-sm text-slate-800">
            Há dispensa de sanção registrada para este mês, então o desconto que incide na nota é de{' '}
            <span className="font-semibold">{redutor.redutorAplicado}%</span>. O valor apurado acima continua
            valendo como medição.
          </p>
        )}
      </div>
    </Cartao>
  )
}

/** Um dos três números do desconto, com o rótulo que diz qual é qual. */
function Valor({ rotulo, valor, destacar }: { rotulo: string; valor: string; destacar?: boolean }) {
  return (
    <div className={cn('rounded-lg border px-4 py-3', destacar ? 'border-red-300 bg-red-50' : 'border-slate-200')}>
      <dt className="text-xs text-slate-600">{rotulo}</dt>
      <dd className={cn('text-2xl font-bold tabular-nums', destacar ? 'text-red-900' : 'text-slate-900')}>{valor}</dd>
    </div>
  )
}
